/* eslint-disable @typescript-eslint/no-explicit-any */

import { fetchJson } from "../fetch-json";
import { DEFAULT_BASE_URL } from "../constants";
import { THttpMethod } from "../types";
import { paths } from "./openapi-spec";

export const makeOpenApiMethod = <
  TPathName extends keyof paths,
  TMethod extends keyof paths[TPathName],
  TStatusCode extends paths[TPathName][TMethod] extends { responses: any }
    ? keyof paths[TPathName][TMethod]["responses"]
    : never
>(
  pathName: TPathName,
  method: TMethod,
  okHttpStatus: TStatusCode
) => {
  type TRouteSpec = paths[typeof pathName];
  type TRouteMethod = TRouteSpec[typeof method];
  type TParams = "parameters" extends keyof TRouteMethod ? TRouteMethod["parameters"] : never;
  type TPathParams = TParams extends { path: any } ? TParams["path"] : Record<string, never>;
  type TQueryParams = TParams extends { query: any } ? TParams["query"] : Record<string, never>;
  type TRequestBody = TRouteMethod extends { requestBody: { content: { "application/json": any } } }
    ? TRouteMethod["requestBody"]["content"]["application/json"]
    : never;
  type TAllResponses = TRouteMethod extends { responses: any } ? TRouteMethod["responses"] : never;
  type TOkResponse = TAllResponses[typeof okHttpStatus] extends { content: { "application/json": any } }
    ? TAllResponses[typeof okHttpStatus]["content"]["application/json"]
    : never;
  const openApi = {
    [pathName]: async function ({
      headers = {},
      baseUrl = DEFAULT_BASE_URL,
      apiRoot = "/api/v3",
      pathParams = {},
      queryParams = {},
      requestBody,
    }: {
      baseUrl?: string;
      apiRoot?: string;
      pathParams?: TPathParams;
      queryParams?: TQueryParams;
      requestBody?: TRequestBody;
      headers?: Record<string, string>;
    }) {
      const pathWithParams: string = Object.entries(pathParams).reduce((newPath: string, [k, v]) => {
        return newPath.replace(`{${k}}`, v as string);
      }, pathName as string);
      const cleanQueryParams = JSON.parse(JSON.stringify(queryParams));
      const search = new URLSearchParams(cleanQueryParams as unknown as Record<string, string>).toString();
      const url = baseUrl + apiRoot + pathWithParams + (search && `?${search}`);
      const options = { headers, method: method as THttpMethod, body: JSON.stringify(requestBody) };

      return fetchJson<TOkResponse>(url, options);
    },
  };

  // Use object to create function with dynamic name, for better stacktraces
  return openApi[pathName];
};
